import { Radio, Wifi, WifiOff } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { formatBaht, request } from "../lib/api";

interface OverlayData { id: string; name: string; campaign_title: string | null; target_amount_satang: number; current_confirmed_amount_satang: number; percentage: number; visual_fill_percentage: number; image_url: string | null; snapshot: { theme?: string }; websocket_url: string; polling_url: string }
interface LiveEnvelope { type: string; event?: { id: string; sequence: number; type: string; environment: string; verification_source: string; data: Record<string, unknown> }; events?: Array<{ id: string; sequence: number; type: string; environment: string; verification_source: string; data: Record<string, unknown> }>; last_sequence?: number }

export function OverlayPage() {
  const { token = "" } = useParams();
  const [data, setData] = useState<OverlayData | null>(null);
  const [connection, setConnection] = useState<"connecting" | "live" | "polling" | "offline">("connecting");
  const [total, setTotal] = useState(0);
  const [previewDelta, setPreviewDelta] = useState(0);
  const [alert, setAlert] = useState<{ donor: string; amount: number; message: string; test: boolean } | null>(null);
  const seen = useRef(new Set<string>());
  const reconnect = useRef(0);
  const lastSequence = useRef(0);
  const totalRef = useRef(0);

  useEffect(() => {
    document.body.classList.add("overlay-body");
    void request<OverlayData>("/api/public/overlays/" + encodeURIComponent(token)).then((result) => {
      setData(result);
      setTotal(result.current_confirmed_amount_satang);
      totalRef.current = result.current_confirmed_amount_satang;
    }).catch(() => setConnection("offline"));
    return () => document.body.classList.remove("overlay-body");
  }, [token]);

  useEffect(() => {
    if (data === null) return;
    let socket: WebSocket | null = null;
    let heartbeat = 0;
    let retryTimer = 0;
    let stopped = false;
    const handleEvent = (event: NonNullable<LiveEnvelope["event"]>) => {
      if (seen.current.has(event.id)) return;
      seen.current.add(event.id);
      lastSequence.current = Math.max(lastSequence.current, event.sequence);
      const amount = typeof event.data.amount_satang === "number" ? event.data.amount_satang : 0;
      const test = event.verification_source === "SIMULATION" || event.environment === "TEST";
      if (event.type === "donation.confirmed" && !test) {
        totalRef.current += amount;
        setTotal(totalRef.current);
      }
      if (test && ["goal.updated","goal.reached","goal.exceeded"].includes(event.type)) setPreviewDelta(event.type === "goal.exceeded" ? Math.max(amount, data.target_amount_satang * 0.25) : event.type === "goal.reached" ? Math.max(0, data.target_amount_satang - totalRef.current) : amount);
      if (event.type === "donation.alert" || event.type === "donation.confirmed") {
        const donor = typeof event.data.donor_name === "string" ? event.data.donor_name : "ผู้สนับสนุน";
        const message = typeof event.data.message === "string" ? event.data.message : "";
        setAlert({ donor, amount, message, test });
        window.setTimeout(() => setAlert(null), 6000);
      }
    };
    const poll = async () => {
      if (stopped) return;
      setConnection("polling");
      try {
        const payload = await request<{ events: NonNullable<LiveEnvelope["events"]>; last_sequence: number }>(data.polling_url + "?since=" + lastSequence.current);
        payload.events.forEach(handleEvent);
        lastSequence.current = Math.max(lastSequence.current, payload.last_sequence);
      } catch { setConnection("offline"); }
      retryTimer = window.setTimeout(() => { void poll(); }, 3000);
    };
    const connect = () => {
      setConnection("connecting");
      socket = new WebSocket(data.websocket_url + "?since=" + lastSequence.current);
      socket.onopen = () => { reconnect.current = 0; setConnection("live"); heartbeat = window.setInterval(() => socket?.readyState === WebSocket.OPEN && socket.send("ping"), 20_000); };
      socket.onmessage = (message) => {
        if (message.data === "pong") return;
        try {
          const payload = JSON.parse(String(message.data)) as LiveEnvelope;
          payload.events?.forEach(handleEvent);
          if (payload.event !== undefined) handleEvent(payload.event);
          if (payload.last_sequence !== undefined) lastSequence.current = Math.max(lastSequence.current, payload.last_sequence);
        } catch { /* Ignore malformed frames. */ }
      };
      socket.onerror = () => socket?.close();
      socket.onclose = () => {
        window.clearInterval(heartbeat);
        if (stopped) return;
        reconnect.current += 1;
        if (reconnect.current <= 3) retryTimer = window.setTimeout(connect, Math.min(5000, 500 * 2 ** reconnect.current));
        else void poll();
      };
    };
    connect();
    return () => { stopped = true; window.clearInterval(heartbeat); window.clearTimeout(retryTimer); socket?.close(); };
  }, [data]);

  if (data === null) return <main className="overlay-loading"><Radio /><span>{connection === "offline" ? "Overlay unavailable" : "Connecting live channel…"}</span></main>;
  const shownTotal = total + previewDelta;
  const percentage = data.target_amount_satang > 0 ? shownTotal / data.target_amount_satang * 100 : 0;
  return <main className={"public-overlay theme-" + String(data.snapshot.theme ?? "thai-modern")}><div className="overlay-connection">{connection === "live" ? <Wifi /> : <WifiOff />}<span>{connection}</span>{previewDelta > 0 && <b>TEST PREVIEW</b>}</div>{data.image_url !== null && <img className="overlay-campaign-image" src={data.image_url} alt="" />}<section className="live-goal-widget"><header><span><Radio />LIVE GOAL</span><small>{data.campaign_title ?? data.name}</small></header><div className="goal-numbers"><strong>{formatBaht(shownTotal)}</strong><span>/ {formatBaht(data.target_amount_satang)}</span><b>{Math.round(percentage)}%</b></div><div className="overlay-progress"><i style={{ width: Math.min(100, percentage) + "%" }} />{percentage > 100 && <em>GOAL +{Math.round(percentage - 100)}%</em>}</div><footer><span>Confirmed total</span><span>{previewDelta > 0 ? "SIMULATION · does not modify Live" : "paymeQR live channel"}</span></footer></section>{alert !== null && <aside className="donation-alert" role="status"><div><BellIcon /></div><p>{alert.test && <b>TEST</b>}<span>{alert.donor}</span><strong>สนับสนุน {formatBaht(alert.amount)}</strong><small>{alert.message}</small></p></aside>}</main>;
}

function BellIcon() { return <><i /><i /><Radio /></>; }
